"use client";

import { countChecklistItems } from "@myaccount/_client/hooks/useChecklistCounts";
import { gtmSendEvent } from "@wojo/analytics";
import { useI18n } from "@wojo/localization";
import { StoryblokImage } from "@wojo/storyblok";
import { Card, LoadingSkeleton, Text } from "@wojo/ui";
import { useLatestTermSuspenseQuery } from "client/hooks/use-latest-term-suspense-query";
import cn from "clsx";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
    CurrencyCode,
    MyAccountBasicDepartureFragment,
    MyAccountBasicMyTripFragment,
    TermMarket,
    TermType,
    TripCustomerType,
} from "generated/graphql";
import React, { Suspense, useId, useState } from "react";
import { TermsAndConditionsModal } from "../terms-and-conditions-modal";
import styles from "./TripCard.module.scss";
import { TripCardStatusLabel } from "./TripCardStatusLabel";
import Link from "next/link";

dayjs.extend(utc);

export type TripCardProps = React.PropsWithChildren<{
    trip?: MyAccountBasicMyTripFragment;
    isPastTrip?: boolean;
    gcDepartureId?: string;
    tourName?: string;
    departure: MyAccountBasicDepartureFragment;
}>;

export function formatDateRange(departureDate: string, endDate: string) {
    const departure = dayjs.utc(departureDate);
    const end = dayjs.utc(endDate);

    const departureFormat = "MMM D";
    const endFormat = "MMM D, YYYY";

    if (departure.month() === end.month()) {
        return `${departure.format(departureFormat)}–${end.format("D, YYYY")}`;
    } else {
        return `${departure.format(departureFormat)}–${end.format(endFormat)}`;
    }
}

const TripCardContent: React.FC<TripCardProps> = ({
    trip,
    isPastTrip,
    departure,
    tourName,
}) => {
    const checklistItems = (trip?.booking?.trips ?? [])
        .map(countChecklistItems)
        .reduce(
            (acc, item) => ({
                recommended: acc.recommended + item.recommended,
                required: acc.required + item.required,
            }),
            { recommended: 0, required: 0 },
        );
    const tourNameId = useId();
    const dateRangeId = useId();
    const l10n = useI18n("account").account;
    const [isModalOpen, setIsModalOpen] = useState(false);

    const term = useLatestTermSuspenseQuery({
        skip: trip?.hasSignedTerms || isPastTrip,

        termType:
            trip?.customerType === TripCustomerType.GroupCoordinator
                ? TermType.GroupCoordinator
                : TermType.General,
        market:
            trip?.booking?.currencyCode === CurrencyCode.Usd
                ? TermMarket.Us
                : TermMarket.Ca,
    });

    const { activeExtensionContent, tourContent, tourCode } = departure;
    const { departureDate, endDate } = trip ?? departure;
    const formattedDateRange = formatDateRange(departureDate, endDate);

    const tourOverviewLink = trip?.booking
        ? l10n.navigation.booking.bookingBaseUrl.l({
              bookingId: trip.booking._id,
          })
        : undefined;
    const extensionNameLine = l10n.withNamedExtensions.l({
        extensionName:
            activeExtensionContent?.name || l10n.tourNameUnavailableLabel.l(),
    });

    return (
        <>
            <Card
                className={styles.card}
                aria-labelledby={`${tourNameId} ${dateRangeId}`}
            >
                <div
                    className={cn(styles.image, {
                        [styles["past-trips"]]: isPastTrip,
                    })}
                >
                    <StoryblokImage
                        coverContainer
                        mobile={{
                            asset: tourContent?.cardImage?.filename
                                ? tourContent.cardImage
                                : {
                                      alt: "",
                                      filename:
                                          l10n.myTours.tripCard.imagePlaceholder.l(),
                                  },
                        }}
                    />
                </div>
                <div className={styles.content}>
                    <div className={styles.body}>
                        <Text.Detail>{formattedDateRange}</Text.Detail>
                        <Text.Heading
                            order="5"
                            tag="p"
                            className={styles["trip-name"]}
                        >
                            {(isPastTrip || trip?.hasSignedTerms) &&
                                (tourOverviewLink ? (
                                    <Link
                                        href={tourOverviewLink}
                                        onClick={() =>
                                            gtmSendEvent({
                                                event: "track",
                                                ga4Tracking: {
                                                    ga4_object: "MY_TOUR",
                                                    ga4_action: "CLICKED",
                                                    tourCode: tourCode,
                                                },
                                            })
                                        }
                                    >
                                        {tourName}
                                        <span
                                            className={styles["focus-ring"]}
                                        />
                                    </Link>
                                ) : (
                                    tourName
                                ))}
                            {!isPastTrip && trip && !trip.hasSignedTerms && (
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(true)}
                                >
                                    <span aria-hidden>{tourName}</span>
                                    <span className="visually-hidden">
                                        {l10n.myTours.tripCard.signTermsScreenReaderTourName.l(
                                            {
                                                tourName:
                                                    tourName ??
                                                    l10n.tourNameUnavailableLabel.l(),
                                            },
                                        )}
                                    </span>
                                    <span className={styles["focus-ring"]} />
                                </button>
                            )}
                        </Text.Heading>
                        {activeExtensionContent &&
                            (!trip || trip.openExtensionLineItem) && (
                                <div className={styles["extension-container"]}>
                                    <Text.Body
                                        className={styles.extension}
                                        title={extensionNameLine}
                                    >
                                        {extensionNameLine}
                                    </Text.Body>
                                </div>
                            )}
                    </div>
                    {trip && (
                        <TripCardStatusLabel
                            status={trip.status}
                            uncheckedItemsCount={checklistItems}
                            hasSignedTerms={trip.hasSignedTerms}
                            isPastTrip={isPastTrip}
                            reviewInfo={{
                                canBeReviewed: trip.canBeReviewed ?? false,
                                isReviewSubmitted: !!trip.review,
                            }}
                        />
                    )}
                </div>
            </Card>
            {trip && (
                <TermsAndConditionsModal
                    isOpen={isModalOpen}
                    onCloseRequested={() => setIsModalOpen(false)}
                    successScreenRedirectLink={tourOverviewLink || "#"}
                    term={term}
                    trip={trip}
                />
            )}
        </>
    );
};

export const TripCard: React.FC<TripCardProps> = (props) => {
    const l10n = useI18n("account").account;

    return (
        <Suspense
            fallback={
                <LoadingSkeleton
                    accessibleText={l10n.myTours.tripCard.loadingFallback.l({
                        tourName:
                            props.tourName ?? l10n.tourNameUnavailableLabel.l(),
                    })}
                    className={styles["card--skeleton"]}
                />
            }
        >
            <TripCardContent {...props} />
        </Suspense>
    );
};
